import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreditCard, Plus, Search, UsersRound, Loader2, Download, BarChart3, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import SubscriptionStats from "@/components/admin/subscriptions/SubscriptionStats";
import SubscriptionCharts from "@/components/admin/subscriptions/SubscriptionCharts";
import SubscriptionTable from "@/components/admin/subscriptions/SubscriptionTable";
import SubscriptionDetailsDialog from "@/components/admin/subscriptions/SubscriptionDetailsDialog";
import AddSubscriptionDialog from "@/components/admin/subscriptions/AddSubscriptionDialog";
import EditSubscriptionDialog from "@/components/admin/subscriptions/EditSubscriptionDialog";
import RenewSubscriptionDialog from "@/components/admin/subscriptions/RenewSubscriptionDialog";
import { exportToCSV } from "@/lib/export-utils";
import SubscriptionRequestsTab from "@/components/admin/subscriptions/SubscriptionRequestsTab";

const AdminSubscriptions = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterCycle, setFilterCycle] = useState("all");
  const [filterPricing, setFilterPricing] = useState("all");

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSub, setEditSub] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsSub, setDetailsSub] = useState<any>(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewSub, setRenewSub] = useState<any>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Group dialog
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupDiscount, setGroupDiscount] = useState("10");
  const [groupNotes, setGroupNotes] = useState("");

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ["admin-subscriptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscriptions")
        .select("*, nurseries(name, logo_url)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: nurseries } = useQuery({
    queryKey: ["admin-nurseries-for-sub"],
    queryFn: async () => {
      const { data } = await supabase.from("nurseries").select("id, name");
      return data || [];
    },
  });

  const { data: groups, refetch: refetchGroups } = useQuery({
    queryKey: ["subscription-groups"],
    queryFn: async () => {
      const { data } = await supabase.from("subscription_groups").select("*").order("created_at", { ascending: false });
      return data || [];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, unknown> }) => {
      const { error } = await supabase.from("nursery_subscriptions").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
      toast.success("تم تحديث الاشتراك");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("nursery_subscriptions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
      toast.success("تم حذف الاشتراك");
      setDeleteOpen(false);
      setDeletingId(null);
    },
    onError: (err: any) => toast.error("خطأ: " + err.message),
  });

  const stats = useMemo(() => {
    if (!subscriptions) return { total: 0, active: 0, revenue: 0, trials: 0, expiringSoon: 0, suspended: 0, monthlyRevenue: 0, yearlyRevenue: 0, perChildCount: 0, perChildRevenue: 0 };
    const now = new Date();
    const in30Days = new Date(now.getTime() + 30 * 86400000);
    const activeSubs = subscriptions.filter((s) => s.status === "active");
    const perChildSubs = activeSubs.filter((s) => (s as any).pricing_model === "per_child");
    return {
      total: subscriptions.length,
      active: activeSubs.length,
      trials: subscriptions.filter((s) => (s as any).is_trial).length,
      revenue: activeSubs.reduce((sum, s) => sum + (s.amount || 0), 0),
      monthlyRevenue: activeSubs.filter((s) => s.billing_cycle === "monthly").reduce((sum, s) => sum + (s.amount || 0), 0),
      yearlyRevenue: activeSubs.filter((s) => s.billing_cycle === "yearly").reduce((sum, s) => sum + (s.amount || 0), 0),
      suspended: subscriptions.filter((s) => s.status === "suspended").length,
      expiringSoon: subscriptions.filter((s) => s.status === "active" && s.expires_at && new Date(s.expires_at) <= in30Days && new Date(s.expires_at) > now).length,
      perChildCount: perChildSubs.length,
      perChildRevenue: perChildSubs.reduce((sum, s) => sum + (s.amount || 0), 0),
    };
  }, [subscriptions]);

  const filtered = useMemo(() => {
    if (!subscriptions) return [];
    return subscriptions.filter((s) => {
      const name = (s as any).nurseries?.name || "";
      if (searchTerm && !name.includes(searchTerm) && !s.plan.includes(searchTerm)) return false;
      if (filterStatus === "trial") return (s as any).is_trial;
      if (filterStatus !== "all" && s.status !== filterStatus) return false;
      if (filterPlan !== "all" && s.plan !== filterPlan) return false;
      if (filterCycle !== "all" && s.billing_cycle !== filterCycle) return false;
      if (filterPricing !== "all") {
        const pm = (s as any).pricing_model || "fixed";
        if (filterPricing !== pm) return false;
      }
      return true;
    });
  }, [subscriptions, searchTerm, filterStatus, filterPlan, filterCycle, filterPricing]);

  const handleExport = () => {
    if (!filtered.length) return;
    const rows = filtered.map((s) => ({
      "الحضانة": (s as any).nurseries?.name || "",
      "الخطة": s.plan,
      "المبلغ": s.amount,
      "الدورة": s.billing_cycle,
      "الحالة": s.status,
      "تاريخ البدء": s.started_at ? format(new Date(s.started_at), "dd/MM/yyyy") : "",
      "الانتهاء": s.expires_at ? format(new Date(s.expires_at), "dd/MM/yyyy") : "",
      "طريقة الدفع": s.payment_method || "",
      "خصم": (s as any).discount_percent || 0,
    }));
    exportToCSV(rows, "subscriptions-report");
    toast.success("تم تصدير التقرير");
  };

  const handleAddGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName) return;
    setGroupLoading(true);
    const { error } = await supabase.from("subscription_groups").insert({
      name: groupName, discount_percent: parseFloat(groupDiscount) || 10, notes: groupNotes || null,
    } as any);
    setGroupLoading(false);
    if (error) toast.error("خطأ: " + error.message);
    else { toast.success("تم إنشاء المجموعة"); refetchGroups(); setGroupOpen(false); setGroupName(""); setGroupDiscount("10"); setGroupNotes(""); }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">إدارة الاشتراكات والدفع</h1>
          <p className="text-muted-foreground text-sm">متابعة شاملة لاشتراكات الحضانات والإيرادات والخصومات</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!filtered.length}>
            <Download className="w-4 h-4 ml-1" />
            تصدير CSV
          </Button>
          <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <UsersRound className="w-4 h-4 ml-1" />
                مجموعة خصم
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm" dir="rtl">
              <DialogHeader><DialogTitle>إنشاء مجموعة خصم</DialogTitle></DialogHeader>
              <form onSubmit={handleAddGroup} className="space-y-4">
                <div><Label>اسم المجموعة *</Label><Input value={groupName} onChange={(e) => setGroupName(e.target.value)} required /></div>
                <div><Label>نسبة الخصم (%)</Label><Input type="number" value={groupDiscount} onChange={(e) => setGroupDiscount(e.target.value)} min={1} max={50} dir="ltr" /></div>
                <div><Label>ملاحظات</Label><Textarea value={groupNotes} onChange={(e) => setGroupNotes(e.target.value)} rows={2} /></div>
                <Button type="submit" className="w-full" disabled={groupLoading}>
                  {groupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "إنشاء المجموعة"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <Button className="gradient-hero border-0" size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="w-4 h-4 ml-1" />
            إنشاء اشتراك
          </Button>
        </div>
      </div>

      {/* Stats */}
      <SubscriptionStats {...stats} />

      <Tabs defaultValue="subscriptions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscriptions">
            <CreditCard className="w-4 h-4 ml-1" />
            الاشتراكات
          </TabsTrigger>
          <TabsTrigger value="charts">
            <BarChart3 className="w-4 h-4 ml-1" />
            الرسوم البيانية
          </TabsTrigger>
          <TabsTrigger value="requests">
            <Clock className="w-4 h-4 ml-1" />
            طلبات الاشتراك
          </TabsTrigger>
          <TabsTrigger value="groups">
            <UsersRound className="w-4 h-4 ml-1" />
            مجموعات الخصم
          </TabsTrigger>
        </TabsList>

        <TabsContent value="subscriptions" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="بحث باسم الحضانة..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pr-9" />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36"><SelectValue placeholder="الحالة" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الحالات</SelectItem>
                    <SelectItem value="active">نشط</SelectItem>
                    <SelectItem value="trial">تجربة مجانية</SelectItem>
                    <SelectItem value="suspended">معلق</SelectItem>
                    <SelectItem value="expired">منتهي</SelectItem>
                    <SelectItem value="cancelled">ملغي</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                  <SelectTrigger className="w-32"><SelectValue placeholder="الخطة" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الخطط</SelectItem>
                    <SelectItem value="basic">أساسي</SelectItem>
                    <SelectItem value="standard">قياسي</SelectItem>
                    <SelectItem value="premium">مميز</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterCycle} onValueChange={setFilterCycle}>
                  <SelectTrigger className="w-28"><SelectValue placeholder="الدورة" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    <SelectItem value="monthly">شهري</SelectItem>
                    <SelectItem value="yearly">سنوي</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterPricing} onValueChange={setFilterPricing}>
                  <SelectTrigger className="w-32"><SelectValue placeholder="التسعير" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">كل التسعير</SelectItem>
                    <SelectItem value="fixed">ثابت (باقة)</SelectItem>
                    <SelectItem value="per_child">بالطفل</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                الاشتراكات ({filtered.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SubscriptionTable
                subscriptions={filtered}
                isLoading={isLoading}
                onStatusChange={(id, status) => updateMutation.mutate({ id, updates: { status } })}
                onEdit={(sub) => { setEditSub(sub); setEditOpen(true); }}
                onDelete={(id) => { setDeletingId(id); setDeleteOpen(true); }}
                onViewDetails={(sub) => { setDetailsSub(sub); setDetailsOpen(true); }}
                onRenew={(sub) => { setRenewSub(sub); setRenewOpen(true); }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="charts">
          <SubscriptionCharts subscriptions={subscriptions || []} />
        </TabsContent>

        <TabsContent value="requests">
          <SubscriptionRequestsTab />
        </TabsContent>

        <TabsContent value="groups">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UsersRound className="w-5 h-5" />
                مجموعات الخصم ({groups?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {groups && groups.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>اسم المجموعة</TableHead>
                      <TableHead>نسبة الخصم</TableHead>
                      <TableHead>عدد الاشتراكات</TableHead>
                      <TableHead>ملاحظات</TableHead>
                      <TableHead>تاريخ الإنشاء</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groups.map((g: any) => {
                      const groupSubs = subscriptions?.filter((s) => (s as any).group_id === g.id) || [];
                      return (
                        <TableRow key={g.id}>
                          <TableCell className="font-medium">{g.name}</TableCell>
                          <TableCell><Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">{g.discount_percent}%</Badge></TableCell>
                          <TableCell>{groupSubs.length}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{g.notes || "—"}</TableCell>
                          <TableCell>{format(new Date(g.created_at), "dd/MM/yyyy")}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center py-8 text-muted-foreground">لا توجد مجموعات خصم بعد</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddSubscriptionDialog open={addOpen} onOpenChange={setAddOpen} nurseries={nurseries || []} groups={groups || []} />
      <EditSubscriptionDialog open={editOpen} onOpenChange={setEditOpen} subscription={editSub} />
      <SubscriptionDetailsDialog open={detailsOpen} onOpenChange={setDetailsOpen} subscription={detailsSub} />
      <RenewSubscriptionDialog open={renewOpen} onOpenChange={setRenewOpen} subscription={renewSub} />

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm" dir="rtl">
          <DialogHeader><DialogTitle>حذف الاشتراك</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">هل أنت متأكد من حذف هذا الاشتراك؟ لا يمكن التراجع عن هذا الإجراء.</p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>إلغاء</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => deletingId && deleteMutation.mutate(deletingId)}>
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "حذف"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptions;
